import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import {
  Box,
  Card,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Button,
  Breadcrumbs,
  Link,
  Fade,
  Container
} from '@mui/material';
import {
  Groups as GroupsIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as AccessTimeIcon,
  PersonPin as PersonPinIcon
} from '@mui/icons-material';

import { Iconify } from 'src/components/iconify';
import { getBranchById, type BranchDto } from 'src/api/branch_api_data';
import { branchDashboardApi, type BranchDashboardStats } from 'src/api/branch-dashboard.api';
import { getUserRoleFromToken } from 'src/utils/jwt';
import SuspiciousLoginTable from 'src/sections/suspicious-login/SuspiciousLoginTable';
import BranchEmployeesTab from '../components/BranchEmployeesTab';
import BranchStatisticsTab from '../components/BranchStatisticsTab';
import BranchAttendanceTab from '../components/BranchAttendanceTab';
import BranchLeaveManagementTab from '../components/BranchLeaveManagementTab';
import BranchManagersTab from '../components/BranchManagersTab';
import BranchEmployeeTransferTab from '../components/BranchEmployeeTransferTab';
import BranchQrCodesTab from '../components/BranchQrCodesTab';
import BranchShiftsTab from '../components/BranchShiftsTab';
import { QrCodeGenerationDialog } from '../components/QrCodeGenerationDialog';
import {
  AnimatedTabPanel,
  EnhancedStatsCard,
  LoadingOverlay,
  StatsCardSkeleton
} from '../components/BranchEnhancedComponents';

// TabPanel removed - using AnimatedTabPanel now

export default function BranchDetailView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [branch, setBranch] = useState<BranchDto | null>(null);
  const [dashboardStats, setDashboardStats] = useState<BranchDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrRefreshKey, setQrRefreshKey] = useState(0);

  const userRole = getUserRoleFromToken();
  const isAdmin = userRole === 'ROLE_ADMIN' || userRole === 'ROLE_SUPER_ADMIN';

  useEffect(() => {
    const fetchBranchData = async () => {
      if (!id) {
        setError('Şube ID bulunamadı');
        setLoading(false);
        setStatsLoading(false);
        return;
      }

      try {
        setLoading(true);
        setStatsLoading(true);
        
        // Paralel API çağrıları
        const [branchData, statsData] = await Promise.allSettled([
          getBranchById(id),
          branchDashboardApi.getDashboardStats(id)
        ]);

        // Branch verilerini işle
        if (branchData.status === 'fulfilled') {
          setBranch(branchData.value);
        } else {
          console.error('Branch fetch error:', branchData.reason);
          setError(branchData.reason?.message || 'Şube bilgileri yüklenemedi');
        }

        // Stats verilerini işle
        if (statsData.status === 'fulfilled') {
          setDashboardStats(statsData.value);
        } else {
          console.error('Stats fetch error:', statsData.reason);
          // Stats yüklenemezse sadece warning ver, error'a düşürme
        }

      } catch (err: any) {
        setError(err.message || 'Veriler yüklenirken bir hata oluştu');
        setBranch(null);
        setDashboardStats(null);
      } finally {
        setLoading(false);
        setStatsLoading(false);
      }
    };

    fetchBranchData();
  }, [id]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  if (loading) {
    return (
      <Container maxWidth={false}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error || !branch) {
    return (
      <Container maxWidth={false}>
        <Box mt={5}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || 'Şube bulunamadı'}
          </Alert>
          <Button 
            variant="contained" 
            startIcon={<Iconify icon="eva:arrow-back-fill" />}
            onClick={() => navigate('/dashboard/branches')}
          >
            Şubelere Dön
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false}>
      <Box mb={{ xs: 2, sm: 3 }}>
        <Breadcrumbs>
          <Link 
            color="inherit" 
            href="/dashboard" 
            onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }}
            sx={{ cursor: 'pointer' }}
          >
            Ana Sayfa
          </Link>
          <Link 
            color="inherit" 
            href="/dashboard/branches"
            onClick={(e) => { e.preventDefault(); navigate('/dashboard/branches'); }}
            sx={{ cursor: 'pointer' }}
          >
            Şubeler
          </Link>
          <Typography color="text.primary">{branch.name}</Typography>
        </Breadcrumbs>
      </Box>

      <Box mb={{ xs: 2, sm: 3 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          justifyContent="space-between" 
          alignItems={{ xs: 'flex-start', sm: 'flex-start' }} 
          spacing={2}
        >
          <Stack spacing={1} sx={{ flex: 1 }}>
            <Typography variant="h3" component="h1">
              {branch.name}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {branch.companyName || 'Şirket bilgisi yok'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {branch.address || 'Adres bilgisi yok'}
            </Typography>
          </Stack>
          
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              minWidth: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'flex-start', sm: 'flex-end' }
            }}
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<Iconify icon="eva:qr-code-outline" />}
              onClick={() => setQrDialogOpen(true)}
              sx={{
                minWidth: { xs: '100%', sm: 'auto' }
              }}
            >
              QR Kod Oluştur
            </Button>
            <Button
              variant="outlined"
              startIcon={<Iconify icon="solar:pen-bold" />}
              onClick={() => navigate(`/dashboard/branches/${branch.id}/edit`)}
              sx={{
                minWidth: { xs: '100%', sm: 'auto' }
              }}
            >
              Düzenle
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Box mb={{ xs: 2, sm: 3 }}>
        <Fade in timeout={800}>
          <Typography variant="h6" gutterBottom>
            Hızlı İstatistikler
          </Typography>
        </Fade>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={{ xs: 1, sm: 2 }}
          sx={{
            '& > *': {
              flex: { xs: 'none', sm: 1 }
            }
          }}
        >
          {statsLoading ? (
            // Loading skeleton
            Array.from({ length: 4 }).map((_, index) => (
              <StatsCardSkeleton key={index} />
            ))
          ) : dashboardStats ? (
            // Real data
            [
              { 
                title: 'Toplam Çalışan', 
                value: dashboardStats.totalEmployees.toString(), 
                icon: <GroupsIcon />, 
                color: 'primary' as const 
              },
              { 
                title: 'Bu Ay Devam', 
                value: `${Math.round(dashboardStats.monthlyAttendanceRate)}%`, 
                icon: <TrendingUpIcon />, 
                color: 'success' as const 
              },
              { 
                title: 'Ortalama Süre', 
                value: `${dashboardStats.averageWorkingHours.toFixed(1)}h`, 
                icon: <AccessTimeIcon />, 
                color: 'warning' as const 
              },
              { 
                title: 'Bugün Aktif', 
                value: dashboardStats.todayActiveEmployees.toString(), 
                icon: <PersonPinIcon />, 
                color: 'info' as const 
              }
            ].map((stat, index) => (
              <EnhancedStatsCard
                key={stat.title}
                title={stat.title}
                value={stat.value}
                icon={stat.icon}
                color={stat.color}
                delay={index * 100}
              />
            ))
          ) : (
            // Fallback data in case of API failure
            [
              { title: 'Toplam Çalışan', value: '-', icon: <GroupsIcon />, color: 'primary' as const },
              { title: 'Bu Ay Devam', value: '-', icon: <TrendingUpIcon />, color: 'success' as const },
              { title: 'Ortalama Süre', value: '-', icon: <AccessTimeIcon />, color: 'warning' as const },
              { title: 'Bugün Aktif', value: '-', icon: <PersonPinIcon />, color: 'info' as const }
            ].map((stat, index) => (
              <EnhancedStatsCard
                key={stat.title}
                title={stat.title}
                value={stat.value}
                icon={stat.icon}
                color={stat.color}
                delay={index * 100}
              />
            ))
          )}
        </Stack>
      </Box>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab label="Çalışanlar" />
            <Tab label="İstatistikler" />
            <Tab label="Devam Kayıtları" />
            <Tab label="İzin Yönetimi" />
            <Tab label="Yöneticiler" />
            <Tab label="Çalışan Transferi" />
            <Tab label="QR Kodları" />
            <Tab label="Şüpheli Girişler" />
            <Tab label="Vardiyalar" />
          </Tabs>
        </Box>
        
        <AnimatedTabPanel value={currentTab} index={0} direction="right">
          <BranchEmployeesTab 
            branchId={branch.id} 
            branchName={branch.name} 
          />
        </AnimatedTabPanel>

        <AnimatedTabPanel value={currentTab} index={1} direction="right">
          <BranchStatisticsTab 
            branchId={branch.id} 
            branchName={branch.name} 
          />
        </AnimatedTabPanel>

        <AnimatedTabPanel value={currentTab} index={2} direction="right">
          <BranchAttendanceTab 
            branchId={branch.id} 
            branchName={branch.name} 
          />
        </AnimatedTabPanel>

        <AnimatedTabPanel value={currentTab} index={3} direction="right">
          <BranchLeaveManagementTab 
            branchId={branch.id} 
            branchName={branch.name} 
          />
        </AnimatedTabPanel>

        <AnimatedTabPanel value={currentTab} index={4} direction="right">
          <BranchManagersTab 
            branchId={branch.id} 
            branchName={branch.name} 
          />
        </AnimatedTabPanel>

        <AnimatedTabPanel value={currentTab} index={5} direction="right">
          <BranchEmployeeTransferTab
            branchId={branch.id}
            branchName={branch.name}
          />
        </AnimatedTabPanel>

        <AnimatedTabPanel value={currentTab} index={6} direction="right">
          <BranchQrCodesTab
            branchId={branch.id}
            branchName={branch.name}
            refreshKey={qrRefreshKey}
          />
        </AnimatedTabPanel>

        <AnimatedTabPanel value={currentTab} index={7} direction="right">
          <SuspiciousLoginTable
            mode="branch"
            branchId={branch.id}
            selectable={isAdmin}
          />
        </AnimatedTabPanel>

        <AnimatedTabPanel value={currentTab} index={8} direction="right">
          <BranchShiftsTab
            branchId={branch.id}
            branchName={branch.name}
          />
        </AnimatedTabPanel>
      </Card>

      {/* QR Code Generation Dialog */}
      {branch && (
        <QrCodeGenerationDialog
          open={qrDialogOpen}
          onClose={() => setQrDialogOpen(false)}
          branch={branch}
          onSuccess={() => {
            // Dialog export için açık kalır; QR sekmesi yeni kodu (ve yalnız onu) göstersin diye yenilenir
            setQrRefreshKey((k) => k + 1);
          }}
        />
      )}
    </Container>
  );
} 